import { vec2 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { Renderer } from '../../../core/render/Renderer';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformBoolean } from '../../../core/utils/uniforms/UniformBoolean';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import { RegionOfInterest } from '../../../data/structure/RegionOfInterest';
import { BooleanOperation } from './SDFOperations';

export enum SDFOperation {
    ADD,
    SUBTRACT,
}

export class SDFOperationRenderer implements Renderer {
    private readonly booleanShader: BooleanShader;

    public constructor() {
        this.booleanShader = new BooleanShader();
    }

    /**
     * Composites the {@link sources} onto the {@link destination} using
     * the blend equation selected by {@link operation} (OR → MAX, AND
     * and AND_NOT → MIN, with the source inverted for AND_NOT).
     *
     * Iterates per Z slice of the destination; for each source, only
     * the destination pixels covered by `source.crossSection(...)` are
     * touched. **Destination pixels outside the source's reach are
     * left unchanged.** This is correct for OR/AND_NOT (nothing to
     * add / subtract = preserve dest) but is *not* a mathematical
     * `dest ∩ source` for AND: when dest extends beyond source, the
     * outside region retains dest's pre-AND content. Callers wanting
     * true-intersection semantics must ensure each destination voxel
     * is covered by some source (e.g. by passing a temp source sized
     * to the destination's grid).
     */
    public renderOnDestination(
        destination: RegionOfInterest | ContourTexture,
        operation: BooleanOperation | SDFOperation,
        ...sources: (RegionOfInterest | ContourTexture)[]
    ) {
        let destTexture: ContourTexture | undefined = undefined;
        if (destination instanceof RegionOfInterest) {
            if (!(destTexture = destination.texture)) {
                throw new Error(
                    `Attempt to render ${sources.length} onto ROI '${destination.name}', but that ROI does not have ContourTexture`
                );
            }
        } else {
            destTexture = destination;
        }
        const sdf = destTexture.signedDistanceField;

        if (!sdf) {
            throw new Error(
                `Attempt to render ${sources.length} ROIs or ContourTextures onto a ContourTexture without a signed distance field`
            );
        }

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        let inverse = false;
        switch (operation) {
            case BooleanOperation.AND_NOT:
                inverse = true;
                gl.blendEquation(gl.MIN);
                break;
            case BooleanOperation.AND:
                gl.blendEquation(gl.MIN);
                break;
            case BooleanOperation.OR:
                gl.blendEquation(gl.MAX);
                break;
            case SDFOperation.ADD:
                gl.blendEquation(gl.FUNC_ADD);
                break;
            case SDFOperation.SUBTRACT:
                gl.blendEquation(gl.FUNC_SUBTRACT);
                break;
        }

        const fbo = new FrameBufferObject(
            vec2.fromValues(sdf.size[0], sdf.size[1])
        );

        const destGrid = destTexture.textureGrid;
        const numberOfSlices = destGrid.sizeZ;

        // Build list of source textures with valid SDFs
        const sourceTextures: ContourTexture[] = [];
        for (const source of sources) {
            const texture =
                source instanceof RegionOfInterest ? source.texture : source;
            if (texture?.signedDistanceField) {
                sourceTextures.push(texture);
            }
        }

        if (sourceTextures.length === 0) {
            return;
        }

        gl.viewport(0, 0, sdf.size[0], sdf.size[1]);
        this.booleanShader.start();
        this.booleanShader.inverse.loadBoolean(inverse);

        for (let k = 0; k < numberOfSlices; ++k) {
            fbo.createTextureLayer(sdf.texture, k);

            const worldToSlice = destGrid.worldToLayerProjection(k);

            for (const srcTexture of sourceTextures) {
                const vao = srcTexture.crossSection(worldToSlice);
                if (vao) {
                    vao.bind(0, 1);
                    srcTexture.bindTexture();
                    gl.drawElements(
                        gl.TRIANGLES,
                        vao.indexCount,
                        gl.UNSIGNED_INT,
                        0
                    );
                    vao.unbind(0, 1);
                    vao.delete();
                }
            }
        }
        destTexture.invalidateCachedPixels();
        this.booleanShader.stop();

        fbo.unbind();
        fbo.delete();
        gl.disable(gl.BLEND);
    }

    public delete(): void {
        this.booleanShader.delete();
    }
}

class BooleanShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
    
    precision highp float;
    
    in vec2 position;
    in vec3 textureCoords;
    
    out vec3 pass_textureCoords;
    
    void main(){
        gl_Position = vec4(position, 0.0, 1.0);
        pass_textureCoords = textureCoords;
    }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es

    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoords;

    out vec4 outColor;

    uniform bool inverse;
    uniform sampler3D sampler;

    void main(){
        outColor = texture(sampler, pass_textureCoords);
        if(inverse){
            outColor.rgb = vec3(1.0) - outColor.rgb;
        }
        outColor.a = 1.0;
    }
    `;

    public readonly inverse: UniformBoolean;

    public constructor() {
        super(
            BooleanShader.VERTEX_CONTENT,
            BooleanShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.inverse = new UniformBoolean('inverse');
        super.storeUniformLocations(this.inverse);
    }
}
